import { CATEGORY_BY_KEY, timeToMinutes, type AgendaBlock } from '@/routes/tools/research-proposal/components/WizardSteps/Step4Proposal/AgendaManager/constants';

// Keep the client-facing full clock format separate from the builder's compact labels.
export function agendaTime(time: string): string {
  const minutes = timeToMinutes(time);
  const hour = Math.floor(minutes / 60) % 24;
  return `${hour % 12 || 12}:${String(minutes % 60).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
}

export function blockEnd(block: AgendaBlock): number {
  const start = timeToMinutes(block.start_time);
  const end = block.end_time ? timeToMinutes(block.end_time) : start;
  return end > start ? end : start + Math.max(block.duration_minutes, 15);
}

export function blockTimeRange(block: AgendaBlock): string {
  const end = blockEnd(block);
  return `${agendaTime(block.start_time)} – ${agendaTime(`${Math.floor(end / 60)}:${end % 60}`)}`;
}

export function sortAgendaBlocks(blocks: AgendaBlock[]): AgendaBlock[] {
  return [...blocks].sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time) || a.sort_order - b.sort_order);
}

// Put simultaneous items beside one another instead of hiding them under the last item.
export function layoutAgendaBlocks(blocks: AgendaBlock[]) {
  const groups: Array<Array<{ block: AgendaBlock; lane: number }>> = [];
  let group: Array<{ block: AgendaBlock; lane: number }> = [];
  let ends: number[] = [];
  let groupEnd = -1;
  for (const block of sortAgendaBlocks(blocks)) {
    const start = timeToMinutes(block.start_time);
    if (start >= groupEnd) {
      group = [];
      groups.push(group);
      ends = [];
    }
    let lane = ends.findIndex(end => end <= start);
    if (lane === -1) lane = ends.length;
    ends[lane] = blockEnd(block);
    group.push({ block, lane });
    groupEnd = Math.max(...ends);
  }
  return groups.flatMap(items => {
    const lanes = Math.max(...items.map(item => item.lane)) + 1;
    return items.map(item => ({ ...item, lanes }));
  });
}

export function agendaCalendarWindow(blocks: AgendaBlock[]) {
  let start = 7 * 60;
  let end = 8 * 60;
  const hourHeight = 40;
  for (const block of blocks) {
    const from = timeToMinutes(block.start_time);
    start = Math.min(start, from);
    end = Math.max(end, blockEnd(block));
    // Keep the overview compact even when an agenda contains a short break.
    // Short items use a marker/title; Schedule and item details retain both times.
  }
  return { startHour: Math.floor(start / 60), endHour: Math.ceil(end / 60), hourHeight };
}

// Width belongs to this day, not the widest overlap anywhere in the agenda.
export function agendaDayWidth(lanes: number): number {
  return lanes > 1 ? lanes * 160 : 176;
}

export function agendaBlockDensity(block: AgendaBlock): 'marker' | 'title' | 'compact' | 'regular' {
  const duration = blockEnd(block) - timeToMinutes(block.start_time);
  if (duration < 30) return 'marker';
  if (duration < 45) return 'title';
  if (duration < 60) return 'compact';
  return 'regular';
}

// Rebel's BlockCard reads activity_categories.color, not its old seed palette.
// These are the current Rebel category values (verified 2026-09-16).
export const REBEL_AGENDA_COLORS: Readonly<Record<string, string>> = {
  transportation: '#6B7B80', meal: '#D4A050', activity: '#4A90B8',
  evening: '#9B6B7A', meeting: '#4A5568', learning: '#C06030',
  workshop: '#B5596B', teambuilding: '#2D8B6F', leisure: '#6B8B6B',
  wellness: '#5D9B8A', excursion: '#8B5D5D',
};

export function agendaBlockColor(block: AgendaBlock): string {
  const saved = block.color?.trim();
  const oldDefault = CATEGORY_BY_KEY[block.category_key]?.color;
  // Remap legacy preset colours at display time; preserve deliberate custom colours.
  if (!saved || saved.toLowerCase() === oldDefault?.toLowerCase()) {
    return REBEL_AGENDA_COLORS[block.category_key] || saved || '#1a4d54';
  }
  return saved;
}

export const AGENDA_BLOCK_GAP = 2;
